import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from 'shared/hooks/useAuth';
import NotFoundPage from 'platform/auth/pages/NotFoundPage';
import { sessionHasPermission } from 'shared/utils/sessionPermissions';
import { buildTenantLoginPath, getTenantIdFromWindowLocation } from 'shared/utils/tenantRoute';
import { getLastLoginTenantId } from 'shared/utils/lastTenantLogin';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';

function normalizeRoleName(roleName) {
  return String(roleName ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
}

function effectiveRoleNames(userPayload) {
  if (!userPayload) return [];
  const { role, roles } = userPayload;
  const list = [];
  if (Array.isArray(roles) && roles.length > 0) {
    list.push(...roles);
  } else if (role) {
    list.push(role);
  }
  return list;
}

/**
 * Guards app routes using the HttpOnly access token (validated via GET /auth/context on boot).
 * - No session / invalid token → tenant login (not a fake 404).
 * - With allowedRoles / allowedPermissions → must also pass role or permission check.
 * - Without role restrictions → any authenticated user may access.
 */
const ProtectedRoute = ({ element, allowedRoles, allowedPermissions }) => {
  const { user, sessionLoading, isAuthenticated } = useAuth();
  if (sessionLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }
  if (!isAuthenticated || !user?.data) {
    const tenantId =
      getTenantIdFromWindowLocation()
      || getRegistryTenantIdFromSession()
      || getLastLoginTenantId();
    return <Navigate to={buildTenantLoginPath(tenantId)} replace />;
  }

  const hasRoleRestrictions =
    (Array.isArray(allowedRoles) && allowedRoles.length > 0) ||
    (Array.isArray(allowedPermissions) && allowedPermissions.length > 0);

  if (!hasRoleRestrictions) {
    return element;
  }

  const names = effectiveRoleNames(user.data);
  const normalizedAllowedRoles = (Array.isArray(allowedRoles) ? allowedRoles : []).map(normalizeRoleName);
  const roleAllowed = names.some((roleName) =>
    normalizedAllowedRoles.includes(normalizeRoleName(roleName))
  );
  const permissionAllowed =
    Array.isArray(allowedPermissions) &&
    allowedPermissions.length > 0 &&
    allowedPermissions.some((code) => sessionHasPermission(user.data, code));

  if (roleAllowed || permissionAllowed) {
    return element;
  }
  return <NotFoundPage />;
};

export default ProtectedRoute;
